// src/pages/SalesPage.js
import { useState, useEffect } from 'react';
import { getProducts, getStores, getSales, createSale, updateProduct } from '../api/apiService';

const bs = { fontFamily: "'Montserrat', sans-serif" };
const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const SalesPage = () => {
  const [allProducts,             setAllProducts]             = useState([]);
  const [stores,                  setStores]                  = useState([]);
  const [selectedStoreId,         setSelectedStoreId]         = useState('');
  const [productsInSelectedStore, setProductsInSelectedStore] = useState([]);
  const [cart,                    setCart]                    = useState([]);
  const [saleNote,                setSaleNote]                = useState('');
  const [salesHistory,            setSalesHistory]            = useState([]);
  const [processing,              setProcessing]              = useState(false);
  const [search,                  setSearch]                  = useState('');
  const [activeTab,               setActiveTab]               = useState('pos'); // 'pos' | 'history'

  useEffect(() => {
    (async () => {
      const [prods, loadedStores, sales] = await Promise.all([
        getProducts(), getStores(), getSales()
      ]);
      setAllProducts(prods);
      setStores(loadedStores);
      setSalesHistory(sales);
      if (loadedStores.length > 0) setSelectedStoreId(String(loadedStores[0].id));
    })();
  }, []);

  useEffect(() => {
    if (selectedStoreId) {
      setProductsInSelectedStore(
        allProducts.filter(p =>
          p.assignedStores && p.assignedStores.map(String).includes(selectedStoreId)
        )
      );
    } else {
      setProductsInSelectedStore([]);
    }
  }, [selectedStoreId, allProducts]);

  const addToCart = (product, variant) => {
    const key = `${product.id}-${variant.size}-${variant.color}`;
    const idx = cart.findIndex(i => i.key === key);
    const storeStock = variant.storeStocks?.[selectedStoreId] || 0;
    const inCart = idx > -1 ? cart[idx].quantity : 0;
    if (storeStock <= inCart) {
      alert(`Sin stock suficiente de ${product.name} (${variant.size} / ${variant.color}).`);
      return;
    }
    if (idx > -1) {
      const updated = [...cart];
      updated[idx].quantity += 1;
      setCart(updated);
    } else {
      setCart(prev => [...prev, {
        key, productId: product.id, productName: product.name,
        variantId: `${variant.size}-${variant.color}`,
        variantInfo: `${variant.size} / ${variant.color}`,
        price: product.price, quantity: 1,
        image: product.images?.[0] || null,
      }]);
    }
  };

  const updateQty = (idx, delta) => {
    const updated = [...cart];
    const newQty = updated[idx].quantity + delta;
    if (newQty <= 0) { setCart(cart.filter((_, i) => i !== idx)); return; }
    updated[idx].quantity = newQty;
    setCart(updated);
  };

  const removeFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));

  const processSale = async () => {
    if (!cart.length || !selectedStoreId) return;
    setProcessing(true);
    try {
      const updatedProducts = allProducts.map(product => {
        const inCart = cart.filter(i => i.productId === product.id);
        if (!inCart.length) return product;
        return {
          ...product,
          variants: product.variants.map(variant => {
            const item = inCart.find(i => i.variantId === `${variant.size}-${variant.color}`);
            if (!item) return variant;
            const newStocks = { ...variant.storeStocks,
              [selectedStoreId]: (variant.storeStocks?.[selectedStoreId] || 0) - item.quantity };
            return { ...variant, storeStocks: newStocks,
              stock: Object.values(newStocks).reduce((a, b) => a + b, 0) };
          })
        };
      });

      const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
      const newSale = {
        date: new Date().toLocaleString(), items: cart, total,
        note: saleNote, storeId: selectedStoreId,
        storeName: stores.find(s => String(s.id) === selectedStoreId)?.name || '',
      };

      for (const p of updatedProducts) await updateProduct(p.id, p);
      await createSale(newSale);

      const [prods, sales] = await Promise.all([getProducts(), getSales()]);
      setAllProducts(prods);
      setSalesHistory(sales);
      setCart([]);
      setSaleNote('');
    } catch (e) {
      alert('Error al procesar la venta.');
      console.error(e);
    } finally {
      setProcessing(false);
    }
  };

  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const storeName = stores.find(s => String(s.id) === selectedStoreId)?.name || '';

  const filteredProducts = productsInSelectedStore.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const TabBtn = ({ id, label, count }) => (
    <button onClick={() => setActiveTab(id)} style={{
      ...bs, background: 'none', border: 'none', cursor: 'pointer',
      padding: '10px 20px', fontSize: '11px', fontWeight: 600,
      letterSpacing: '0.15em', textTransform: 'uppercase',
      color: activeTab === id ? '#961914' : '#aaa',
      borderBottom: activeTab === id ? '2px solid #961914' : '2px solid transparent',
      transition: 'all 0.2s',
    }}>
      {label}{count !== undefined && count > 0 && (
        <span style={{ marginLeft: '6px', background: '#961914', color: 'white',
          borderRadius: '10px', padding: '1px 7px', fontSize: '9px' }}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div style={{ padding: '20px 16px', maxWidth: '1400px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ ...ds, fontSize: '2rem', fontWeight: 400,
            color: '#060D14', margin: '0 0 4px' }}>
            Punto de Venta
          </h1>
          <p style={{ ...bs, fontSize: '12px', color: '#aaa', margin: 0 }}>
            {storeName && `Tienda: ${storeName}`}
          </p>
        </div>

        {/* Selector tienda */}
        <select value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)}
          style={{ ...bs, padding: '9px 14px', border: '1px solid #e5e0e0',
            fontSize: '12px', color: '#060D14', background: 'white',
            cursor: 'pointer', outline: 'none', borderRadius: '2px', minWidth: '180px',
          }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
          {stores.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #f0eaea', marginBottom: '20px' }}>
        <TabBtn id="pos"     label="Nueva Venta" count={cart.length} />
        <TabBtn id="history" label="Historial"   count={salesHistory.length} />
      </div>

      {activeTab === 'pos' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px' }}
          className="flex-col md:grid">

          {/* ── PRODUCTOS ────────────────────────────────────────────── */}
          <div>
            {/* Búsqueda */}
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#bbb"
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar producto…"
                style={{ ...bs, width: '100%', padding: '10px 12px 10px 36px',
                  border: '1px solid #e5e0e0', fontSize: '12px', color: '#060D14',
                  outline: 'none', borderRadius: '2px', boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#961914'}
                onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
            </div>

            {filteredProducts.length === 0 ? (
              <div style={{ background: 'white', border: '1px solid #f0eaea',
                padding: '48px', textAlign: 'center', borderRadius: '2px' }}>
                <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>
                  No hay productos disponibles en esta tienda.
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                {filteredProducts.map(product => (
                  <div key={product.id} style={{
                    background: 'white', border: '1px solid #f0eaea',
                    overflow: 'hidden', borderRadius: '2px',
                  }}>
                    {/* Imagen */}
                    <div style={{ height: '130px', background: '#f8f5f5',
                      overflow: 'hidden' }}>
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', color: '#ddd' }}>
                          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    <div style={{ padding: '12px' }}>
                      <p style={{ ...ds, fontSize: '1rem', fontWeight: 600,
                        color: '#060D14', margin: '0 0 2px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.name}
                      </p>
                      <p style={{ ...ds, fontSize: '1.1rem', color: '#961914',
                        margin: '0 0 10px' }}>
                        ${Number(product.price).toFixed(2)}
                      </p>

                      {/* Variantes */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {product.variants.map((v, i) => {
                          const stock = v.storeStocks?.[selectedStoreId] || 0;
                          return (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center' }}>
                              <div>
                                <span style={{ ...bs, fontSize: '10px', color: '#888' }}>
                                  {v.size} / {v.color}
                                </span>
                                <span style={{ ...bs, fontSize: '10px', marginLeft: '6px',
                                  color: stock > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                  ({stock})
                                </span>
                              </div>
                              <button onClick={() => addToCart(product, v)}
                                disabled={stock <= 0}
                                style={{ ...bs, background: stock > 0 ? '#961914' : '#e5e5e5',
                                  color: stock > 0 ? 'white' : '#bbb', border: 'none',
                                  padding: '4px 10px', fontSize: '10px', fontWeight: 600,
                                  cursor: stock > 0 ? 'pointer' : 'not-allowed',
                                  letterSpacing: '0.1em', borderRadius: '2px',
                                  transition: 'background 0.2s',
                                }}
                                onMouseOver={e => { if (stock > 0) e.currentTarget.style.background = '#7a140f'; }}
                                onMouseOut={e => { if (stock > 0) e.currentTarget.style.background = '#961914'; }}>
                                + Añadir
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── CARRITO ──────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0',
            background: 'white', border: '1px solid #f0eaea',
            borderRadius: '2px', height: 'fit-content', position: 'sticky', top: '20px' }}>

            {/* Header carrito */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0eaea',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ ...ds, fontSize: '1.3rem', fontWeight: 600,
                color: '#060D14', margin: 0 }}>
                Venta Actual
              </h2>
              {cart.length > 0 && (
                <button onClick={() => setCart([])} style={{
                  ...bs, background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '10px', color: '#bbb', letterSpacing: '0.12em',
                  textTransform: 'uppercase', fontWeight: 600,
                }}
                onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                onMouseOut={e => e.currentTarget.style.color = '#bbb'}>
                  Limpiar
                </button>
              )}
            </div>

            {/* Items */}
            <div style={{ maxHeight: '320px', overflowY: 'auto', padding: '8px 0' }}>
              {cart.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p style={{ ...bs, fontSize: '12px', color: '#ccc' }}>
                    Agrega productos para iniciar la venta
                  </p>
                </div>
              ) : (
                cart.map((item, i) => (
                  <div key={item.key} style={{ padding: '10px 20px',
                    borderBottom: '1px solid #faf7f7',
                    display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {item.image && (
                      <img src={item.image} alt={item.productName}
                        style={{ width: '36px', height: '36px', objectFit: 'cover',
                          borderRadius: '2px', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ ...bs, fontSize: '12px', fontWeight: 600,
                        color: '#060D14', margin: '0 0 2px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.productName}
                      </p>
                      <p style={{ ...bs, fontSize: '10px', color: '#bbb', margin: 0 }}>
                        {item.variantInfo}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button onClick={() => updateQty(i, -1)} style={{
                        width: '22px', height: '22px', border: '1px solid #e5e0e0',
                        background: 'none', cursor: 'pointer', fontSize: '14px',
                        color: '#666', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', borderRadius: '2px',
                      }}>−</button>
                      <span style={{ ...bs, fontSize: '12px', fontWeight: 600,
                        color: '#060D14', minWidth: '18px', textAlign: 'center' }}>
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQty(i, 1)} style={{
                        width: '22px', height: '22px', border: '1px solid #e5e0e0',
                        background: 'none', cursor: 'pointer', fontSize: '14px',
                        color: '#666', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', borderRadius: '2px',
                      }}>+</button>
                    </div>
                    <span style={{ ...ds, fontSize: '1rem', color: '#961914',
                      fontWeight: 400, minWidth: '60px', textAlign: 'right' }}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                    <button onClick={() => removeFromCart(i)} style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#ddd', padding: '2px',
                    }}
                    onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                    onMouseOut={e => e.currentTarget.style.color = '#ddd'}>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Nota */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #f0eaea' }}>
              <textarea value={saleNote} onChange={e => setSaleNote(e.target.value)}
                placeholder="Nota de venta (opcional)…"
                rows={2} style={{ ...bs, width: '100%', padding: '8px 12px',
                  border: '1px solid #e5e0e0', fontSize: '12px', color: '#060D14',
                  resize: 'none', outline: 'none', borderRadius: '2px',
                  boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#961914'}
                onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
            </div>

            {/* Total + botón */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid #f0eaea' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ ...bs, fontSize: '11px', fontWeight: 600,
                  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#aaa' }}>
                  Total
                </span>
                <span style={{ ...ds, fontSize: '1.8rem', color: '#060D14', fontWeight: 400 }}>
                  ${total.toFixed(2)}
                </span>
              </div>
              <button onClick={processSale}
                disabled={cart.length === 0 || !selectedStoreId || processing}
                style={{ ...bs, width: '100%', padding: '14px',
                  background: cart.length === 0 ? '#e5e5e5' : '#961914',
                  color: cart.length === 0 ? '#bbb' : 'white',
                  border: 'none', cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em',
                  textTransform: 'uppercase', borderRadius: '2px', transition: 'background 0.2s',
                }}
                onMouseOver={e => { if (cart.length > 0) e.currentTarget.style.background = '#7a140f'; }}
                onMouseOut={e => { if (cart.length > 0) e.currentTarget.style.background = '#961914'; }}>
                {processing ? 'Procesando…' : 'Registrar Venta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORIAL ──────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div>
          {salesHistory.length === 0 ? (
            <div style={{ background: 'white', border: '1px solid #f0eaea',
              padding: '64px', textAlign: 'center', borderRadius: '2px' }}>
              <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>
                No hay ventas registradas aún.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[...salesHistory].reverse().map(sale => (
                <div key={sale.id} style={{ background: 'white',
                  border: '1px solid #f0eaea', borderRadius: '2px', overflow: 'hidden' }}>
                  {/* Header venta */}
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid #faf5f5',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', flexWrap: 'wrap', gap: '8px',
                    background: '#fdfafa' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <span style={{ ...bs, fontSize: '10px', fontWeight: 600,
                        letterSpacing: '0.15em', textTransform: 'uppercase', color: '#bbb' }}>
                        #{sale.id}
                      </span>
                      <span style={{ ...bs, fontSize: '12px', color: '#060D14', fontWeight: 500 }}>
                        {sale.storeName}
                      </span>
                      {sale.note && (
                        <span style={{ ...bs, fontSize: '11px', color: '#aaa',
                          fontStyle: 'italic' }}>
                          "{sale.note}"
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <span style={{ ...bs, fontSize: '11px', color: '#bbb' }}>
                        {sale.date}
                      </span>
                      <span style={{ ...ds, fontSize: '1.3rem', color: '#961914',
                        fontWeight: 400 }}>
                        ${Number(sale.total).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {/* Items */}
                  <div style={{ padding: '12px 20px', display: 'flex',
                    flexWrap: 'wrap', gap: '8px' }}>
                    {(sale.items || []).map((item, i) => (
                      <span key={i} style={{ ...bs, fontSize: '11px', color: '#666',
                        background: '#f8f5f5', padding: '4px 10px', borderRadius: '20px',
                        border: '1px solid #f0eaea' }}>
                        {item.productName} · {item.variantInfo} × {item.quantity}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesPage;
